import { useFrame } from '@react-three/fiber'
import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import { RolodexTilesPlugin } from 'features/overview'
import {
  GLTFExtensionsPlugin,
  ReorientationPlugin,
  TilesPluginR3f,
  TilesRendererR3f,
} from 'gecko-3d-tiles-renderer'
import { MeshoptDecoder } from 'meshoptimizer'
import React, { FC, useEffect, useMemo, useRef, useState } from 'react'
import { Group, Material, Mesh, Object3D } from 'three'

interface TilesLayerProps {
  kestrelModelId: string
  folder: string
  visible: boolean
  opacity?: number
  onLoaded?: () => void
  onRendererReady?: (renderer: unknown) => void
}

/**
 * Set opacity on all materials in an object tree
 */
function setTreeOpacity(object: Object3D, opacity: number): void {
  object.traverse((child) => {
    if (child instanceof Mesh && child.material) {
      const materials = Array.isArray(child.material)
        ? child.material
        : [child.material]
      materials.forEach((mat: Material) => {
        if ('opacity' in mat) {
          mat.opacity = opacity
          mat.transparent = opacity < 1
          mat.needsUpdate = true
        }
      })
    }
  })
}

/**
 * A single 3D tiles layer with visibility and opacity control.
 * Used to render multiple layers for fade-out transitions.
 */
export const TilesLayer: FC<TilesLayerProps> = ({
  kestrelModelId,
  folder,
  visible,
  opacity = 1,
  onLoaded,
  onRendererReady,
}) => {
  const { ROLODEX_URL } = useRuntimeVariables()
  const { authorizedAxios } = useGeckoAuth()
  const [tilesRenderer, setTilesRenderer] = useState<unknown>(null)
  const [decoderReady, setDecoderReady] = useState(false)
  const hasCalledOnLoaded = useRef(false)

  useEffect(() => {
    MeshoptDecoder.ready.then(() => {
      setDecoderReady(true)
    })
  }, [])

  const rolodexPluginOptions = useMemo(() => {
    const rootPath = `3d_tiles/${folder}`
    return {
      entryID: kestrelModelId,
      rootPath: rootPath,
      axios: authorizedAxios,
      rolodexURL: ROLODEX_URL,
    }
  }, [ROLODEX_URL, authorizedAxios, kestrelModelId, folder])

  const reorientationOptions = useMemo(
    () => ({
      up: '-y' as const,
      recenter: false,
    }),
    []
  )

  // Store current opacity in ref for event handlers
  const opacityRef = useRef(opacity)
  opacityRef.current = opacity

  // Handle tiles-load-end event and apply opacity to newly loaded tiles
  useEffect(() => {
    if (!tilesRenderer) return

    const renderer = tilesRenderer as {
      group?: Group
      addEventListener?: (
        event: string,
        callback: (evt: { scene?: Object3D }) => void
      ) => void
      removeEventListener?: (
        event: string,
        callback: (evt: { scene?: Object3D }) => void
      ) => void
    }

    // Apply opacity when any model/tile is loaded
    const handleTileLoad = (evt: { scene?: Object3D }) => {
      if (evt.scene) {
        setTreeOpacity(evt.scene, opacityRef.current)
      }
      // Also apply to entire group to catch any missed tiles
      if (renderer.group) {
        setTreeOpacity(renderer.group, opacityRef.current)
      }
    }

    // Handle initial load complete
    const handleLoadEnd = () => {
      if (!hasCalledOnLoaded.current) {
        hasCalledOnLoaded.current = true
        onLoaded?.()
      }
    }

    if (renderer.addEventListener) {
      renderer.addEventListener('load-model', handleTileLoad)
      renderer.addEventListener('tiles-load-end', handleLoadEnd)
    }

    return () => {
      if (renderer.removeEventListener) {
        renderer.removeEventListener('load-model', handleTileLoad)
        renderer.removeEventListener('tiles-load-end', handleLoadEnd)
      }
    }
  }, [tilesRenderer, onLoaded])

  // Control visibility of the tiles group
  useEffect(() => {
    if (!tilesRenderer) return
    const renderer = tilesRenderer as { group?: Group }
    if (renderer.group) {
      renderer.group.visible = visible
    }
  }, [tilesRenderer, visible])

  // Apply opacity immediately when it changes
  useEffect(() => {
    if (!tilesRenderer) return
    const renderer = tilesRenderer as { group?: Group }
    if (renderer.group) {
      setTreeOpacity(renderer.group, opacity)
    }
  }, [tilesRenderer, opacity])

  // Also continuously apply opacity in useFrame as backup
  useFrame(() => {
    if (!tilesRenderer) return
    const renderer = tilesRenderer as { group?: Group }
    if (renderer.group) {
      setTreeOpacity(renderer.group, opacityRef.current)
    }
  })

  if (!decoderReady) {
    return null
  }

  return (
    <TilesRendererR3f
      url={'tileset.json'}
      errorTarget={2}
      ref={(ref) => {
        setTilesRenderer(ref)
        onRendererReady?.(ref)
      }}>
      <TilesPluginR3f plugin={RolodexTilesPlugin} args={rolodexPluginOptions} />
      <TilesPluginR3f
        plugin={ReorientationPlugin}
        args={reorientationOptions}
      />
      <TilesPluginR3f
        plugin={GLTFExtensionsPlugin}
        args={{ meshoptDecoder: MeshoptDecoder }}
      />
    </TilesRendererR3f>
  )
}
